import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, Subject, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AssetClass, AssetClassSummary } from '@models/asset-class.interface';
import { SaveAssetClassDto } from '@models/save-asset-class-dto.interface';

@Injectable({ providedIn: 'root' })
export class AssetClassesService {
  private readonly apiService = inject(ApiService);
  private listCache$?: Observable<AssetClassSummary[]>;
  private readonly detailCache = new Map<number, Observable<AssetClass>>();
  private readonly changed$ = new Subject<void>();
  readonly changed = this.changed$.asObservable();

  getList(forceRefresh = false): Observable<AssetClassSummary[]> {
    if (forceRefresh || !this.listCache$) {
      this.listCache$ = this.apiService
        .get('assetclasses')
        .pipe(shareReplay(1));
    }

    return this.listCache$;
  }

  getById(id: number, forceRefresh = false): Observable<AssetClass> {
    if (forceRefresh || !this.detailCache.has(id)) {
      const request$ = this.apiService
        .get(`assetclasses/${id}`)
        .pipe(shareReplay(1));
      this.detailCache.set(id, request$);
    }

    return this.detailCache.get(id)!;
  }

  create(dto: SaveAssetClassDto): Observable<AssetClass> {
    return this.apiService
      .post('assetclasses', dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  update(id: number, dto: SaveAssetClassDto): Observable<AssetClass> {
    return this.apiService
      .put(`assetclasses/${id}`, dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  delete(id: number): Observable<void> {
    return this.apiService
      .delete(`assetclasses/${id}`)
      .pipe(tap(() => this.invalidateCache(id)));
  }

  private invalidateCache(id?: number): void {
    this.listCache$ = undefined;
    if (id !== undefined) {
      this.detailCache.delete(id);
    }
    this.changed$.next();
  }
}